import "./UploadLocationDocumentModal.css";

import { ServiceError } from "../../../../../../../service/Shared";
import {
  ActionButtons,
  Column,
  ErrorBox,
  FormLabel,
  KanaTooltip,
  Modal,
  MultiSelect,
  RadioButtonGroup,
  Row,
  SingleFileUpload,
  TextInput,
} from "../../../../../../../widget";
import { RadioChangeEvent } from "../../../../../../../widget/forms/Input/RadioButtonGroup/components";
import { useUploadLocationDocumentModal } from "./useUploadLocationDocumentModal";

interface UploadLocationDocumentModalProps {
  show: boolean;
  closeModal: () => void;
  saveBoundaryFile: (boundaryFile: File, visibility: boolean, selectedOrganisations: string[]) => void;
  drawShapeJson: string;
}

export const UploadLocationDocumentModal = ({
  show,
  closeModal,
  saveBoundaryFile,
  drawShapeJson,
}: UploadLocationDocumentModalProps): JSX.Element => {
  const {
    fileUploadRef,
    maxFileSize,
    selectedDocumentType,
    selectedDocumentVariant,
    isHandleSubmitLoading,
    setFiles,
    handleCancel,
    handleSubmit,
    errors,
    relatedOrganisations,
    visibility,
    setVisibility,
    setSelectedOrganisations,
  } = useUploadLocationDocumentModal({
    show,
    closeModal,
    saveBoundaryFile,
    drawShapeJson,
  });

  return (
    <div className="UploadLocationDocumentModal">
      <Modal show={show} title="Upload location document" onClose={handleCancel}>
        {errors && errors.length > 0 && (
          <Row spacingV="ml" justify="center">
            <Column span={6}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error: ServiceError) => error.message)}
              />
            </Column>
          </Row>
        )}
        <form onSubmit={(e) => handleSubmit(e)}>
          <Row spacingV="l" justify="center">
            <Column span={6} hasNoPadding>
              <TextInput name="Document type" label="Document type" disabled value={selectedDocumentType} />
            </Column>
          </Row>
          <Row spacingV="l" justify="center">
            <Column span={6} hasNoPadding>
              <TextInput name="Variant" label="Variant" disabled value={selectedDocumentVariant} />
            </Column>
          </Row>
          {drawShapeJson ? (
            <Row spacingV="l" justify="center">
              <Column span={6} hasNoPadding>
                <TextInput name="Shape" label="Shape" disabled value="Shape JSON" />
              </Column>
            </Row>
          ) : (
            <Row spacingV="l" justify="center">
              <Column span={6} hasNoPadding>
                <div className="ModalFileUpload">
                  {drawShapeJson}
                  <SingleFileUpload
                    ref={fileUploadRef}
                    label="Upload document"
                    maxFileSize={maxFileSize}
                    onChange={(e) => setFiles([e])}
                  />
                </div>
              </Column>
            </Row>
          )}
          <Row spacingV="l" justify="center">
            <Column span={6} hasNoPadding>
              <div className="ModalRadioButtons">
                <RadioButtonGroup
                  label="Public visibility"
                  tooltip={
                    <KanaTooltip
                      tooltipHeader="What is public visibility?"
                      tooltipText="Choose whether this document is visible in the public directory or not"
                    />
                  }
                  data={[
                    { key: false, value: "Public" },
                    { key: true, value: "Private" },
                  ]}
                  direction="vertical"
                  value={visibility}
                  onChange={(e: RadioChangeEvent) => {
                    setVisibility(e.target.value);
                    setSelectedOrganisations(undefined);
                  }}
                />
              </div>
            </Column>
          </Row>
          {relatedOrganisations && relatedOrganisations?.length !== 0 && visibility && (
            <Row spacingV="l" justify="center">
              <Column span={6} hasNoPadding>
                <FormLabel
                  label="Organisation access"
                  tooltip={
                    <KanaTooltip
                      tooltipHeader="What is organisation access?"
                      tooltipText="If the document is public, all organisations with project access can view the document"
                    />
                  }
                  tooltipJustify="left"
                />
                <div className="ModalDropdown">
                  <MultiSelect data={relatedOrganisations} onChange={(values) => setSelectedOrganisations(values)} />
                </div>
              </Column>
            </Row>
          )}
          <Row justify="center">
            <Column span={6} hasNoPadding>
              <ActionButtons
                submitButtonLabel="Upload document"
                isLoading={isHandleSubmitLoading}
                onCancel={handleCancel}
                fullWidth
              />
            </Column>
          </Row>
        </form>
      </Modal>
    </div>
  );
};
